import { useEffect, useRef, useState, type FC } from 'react';
import { MfaMethod } from '@shared/services';
import { TotpSetupView } from './TotpSetupView';
import { Card } from '@/components/ui';
import { useMfaConfirmMutation, useMfaEnrollMutation, type IMfaConfirmRestView } from '@/shared/api/auth';
import { getLoginFlowError, isMfaSessionExpiredError } from '@/shared/utils';

interface Props {
  mfaPending: string;
  onConfirmed: (view: IMfaConfirmRestView) => void;
  onExpired: () => void;
  onBack: () => void;
}

export const TotpSetup: FC<Props> = ({ mfaPending, onConfirmed, onExpired, onBack }) => {
  const { enroll } = useMfaEnrollMutation();
  const { confirm, isConfirming } = useMfaConfirmMutation();

  const [otpauthUri, setOtpauthUri] = useState<string>();
  const [secret, setSecret] = useState<string>();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  // `enroll` mints a fresh secret on every call, so it must run exactly once — guard against the
  // double-invoke of StrictMode (and any re-render) with a ref rather than an effect dependency.
  const enrolledRef = useRef(false);

  useEffect(() => {
    if (enrolledRef.current) {
      return;
    }

    enrolledRef.current = true;

    void (async () => {
      try {
        const view = await enroll({ mfaPending, method: MfaMethod.TOTP });
        setOtpauthUri(view.otpauthUri);
        setSecret(view.secret);
      } catch (caught) {
        if (isMfaSessionExpiredError(caught)) {
          onExpired();

          return;
        }

        setError(getLoginFlowError(caught));
      }
    })();
  }, [enroll, mfaPending, onExpired]);

  const submit = async () => {
    setError(null);

    try {
      const view = await confirm({ mfaPending, code });
      onConfirmed(view);
    } catch (caught) {
      if (isMfaSessionExpiredError(caught)) {
        onExpired();

        return;
      }

      setError(getLoginFlowError(caught));
    }
  };

  return (
    <>
      <Card.BackButton onClick={onBack} testId="mfa-back-button" />
      <Card.Title
        title="Set up an authenticator app"
        description="Scan the QR code with your authenticator app, or enter the key manually."
      />
      <TotpSetupView
        otpauthUri={otpauthUri}
        secret={secret}
        code={code}
        onCodeChange={setCode}
        onSubmit={submit}
        isSubmitting={isConfirming}
        error={error}
      />
    </>
  );
};
